import { useId, useState } from "react";
import type { JSX } from "react";
import type { Project } from "../../api/schemas";
import type { ActivityOption } from "../../domain/activities";
import type { ClockState } from "../../domain/clock";
import { filterProjects, orderProjects } from "../../domain/projects";
import type { ProjectChoices } from "../../domain/projects";
import { useI18n } from "../../shared/i18n/I18nContext";
import { cx } from "../../shared/ui/classNames";
import { ActivityChoice } from "../projects/ActivityChoice";
import styles from "../../shared/ui/ui.module.css";
import clockStyles from "./ClockPage.module.css";
import type { ClockControl } from "./useClockControl";

/** Properties of {@link ProjectSwitcher}. */
export interface ProjectSwitcherProps {
  /** Current clock state. */
  readonly state: ClockState;
  /** Selectable projects from the offline cache. */
  readonly projects: readonly Project[];
  /** Favorites and recently used projects. */
  readonly choices: ProjectChoices;
  /** The project that applies now, or `null`. */
  readonly currentProjectId: string | null;
  /** The project preselected for the next clock-in, or `null`. */
  readonly clockInProjectId: string | null;
  /** Whether an action is running. */
  readonly busy: boolean;
  /** Called when the project for the next clock-in changes. */
  readonly onClockInProjectChange: (projectId: string | null) => void;
  /** Called when the person switches the project while working. */
  readonly onSwitch: (projectId: string | null) => void;
  /** Called when a project is marked or unmarked as favorite. */
  readonly onToggleFavorite: (projectId: string) => void;
  /** The activities that can be chosen for a project (T-184); without them no activity is offered. */
  readonly activitiesOf?: (projectId: string | null) => readonly ActivityOption[];
  /** The activity preselected for the next clock-in. */
  readonly clockInActivityId?: string | null;
  /** Called when the activity for the next clock-in changes. */
  readonly onClockInActivityChange?: (activityId: string | null) => void;
  /** The activity that applies now. */
  readonly currentActivityId?: string | null;
  /** Called when the person changes the activity of the current project while working. */
  readonly onSwitchActivity?: (activityId: string | null) => void;
  /** Whether the requirement asks for an activity (a hint only, never blocking). */
  readonly requireActivity?: boolean;
  /**
   * Projects whose budget is nearly exhausted (T-185, tenant switch): marked „Budget fast
   * erschöpft“, without numbers; choosing them stays possible.
   */
  readonly nearlyExhausted?: ReadonlySet<string>;
}

/**
 * Project selection on the clock screen (T-124, spec 6.5.4): the project for the next clock-in and,
 * while working, "switch project" with one tap. Favorites and recently used projects come first
 * and projects can be searched by code or name. Switching never changes the working time.
 *
 * @param props - Component properties.
 * @returns The selection.
 */
export function ProjectSwitcher(props: ProjectSwitcherProps): JSX.Element {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectId = useId();
  const searchId = useId();
  const ordered = orderProjects(props.projects, props.choices);
  const current = props.projects.find((project) => project.id === props.currentProjectId) ?? null;
  const label = (project: Project): string =>
    props.nearlyExhausted?.has(project.id) === true
      ? `${project.code} · ${project.name} (${t("project.budgetNearlyExhausted")})`
      : `${project.code} · ${project.name}`;
  const activitiesOf = props.activitiesOf ?? ((): readonly ActivityOption[] => []);
  const requireActivity = props.requireActivity === true;
  const clockInActivities = activitiesOf(props.clockInProjectId);
  const currentActivities = activitiesOf(props.currentProjectId);

  if (props.state === "Off") {
    const clockIn = (
      <section
        className={cx(styles["card"], clockStyles["project"])}
        aria-label={t("project.title")}
      >
        <div className={styles["field"]}>
          <label htmlFor={selectId}>{t("project.forClockIn")}</label>
          <select
            id={selectId}
            value={props.clockInProjectId ?? ""}
            disabled={props.busy}
            onChange={(event) => {
              props.onClockInProjectChange(event.target.value === "" ? null : event.target.value);
            }}
          >
            <option value="">{t("project.none")}</option>
            {ordered.map((project) => (
              <option key={project.id} value={project.id}>
                {label(project)}
              </option>
            ))}
          </select>
        </div>
        {clockInActivities.length === 0 || props.onClockInActivityChange === undefined ? null : (
          <ActivityChoice
            label={t("activity.forClockIn")}
            activities={clockInActivities}
            value={props.clockInActivityId ?? null}
            disabled={props.busy}
            requireActivity={requireActivity}
            onChange={props.onClockInActivityChange}
          />
        )}
      </section>
    );
    return clockIn;
  }

  const matching = filterProjects(ordered, query);
  const choose = (projectId: string | null): void => {
    setOpen(false);
    setQuery("");
    props.onSwitch(projectId);
  };

  const section = (
    <section className={cx(styles["card"], clockStyles["project"])} aria-label={t("project.title")}>
      <p aria-live="polite">
        {current === null
          ? t("project.currentNone")
          : t("project.current", { project: label(current) })}
      </p>
      {props.state === "Working" &&
      currentActivities.length > 0 &&
      props.onSwitchActivity !== undefined ? (
        <ActivityChoice
          label={t("activity.current")}
          activities={currentActivities}
          value={props.currentActivityId ?? null}
          disabled={props.busy}
          requireActivity={requireActivity}
          onChange={props.onSwitchActivity}
        />
      ) : null}
      {props.state === "Working" ? (
        <button
          type="button"
          className={styles["button"]}
          aria-expanded={open}
          disabled={props.busy}
          onClick={() => {
            setOpen(!open);
          }}
        >
          {t("project.switch")}
        </button>
      ) : null}
      {open ? (
        <div className={styles["stack"]}>
          <div className={styles["field"]}>
            <label htmlFor={searchId}>{t("project.search")}</label>
            <input
              id={searchId}
              type="search"
              value={query}
              autoComplete="off"
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
          </div>
          <ul className={clockStyles["projectList"]} aria-label={t("project.list")}>
            {matching.map((project) => {
              const isFavorite = props.choices.favorites.includes(project.id);
              const item = (
                <li key={project.id} className={clockStyles["projectItem"]}>
                  <button
                    type="button"
                    className={cx(
                      styles["button"],
                      project.id === props.currentProjectId ? styles["primary"] : undefined,
                    )}
                    aria-current={project.id === props.currentProjectId ? "true" : undefined}
                    onClick={() => {
                      choose(project.id);
                    }}
                  >
                    {label(project)}
                  </button>
                  <button
                    type="button"
                    className={styles["button"]}
                    aria-pressed={isFavorite}
                    aria-label={t("project.favorite", { project: project.code })}
                    onClick={() => {
                      props.onToggleFavorite(project.id);
                    }}
                  >
                    {isFavorite ? "★" : "☆"}
                  </button>
                </li>
              );
              return item;
            })}
          </ul>
          {matching.length === 0 ? <p className={styles["muted"]}>{t("project.noMatch")}</p> : null}
          {props.currentProjectId === null ? null : (
            <button
              type="button"
              className={styles["button"]}
              onClick={() => {
                choose(null);
              }}
            >
              {t("project.stop")}
            </button>
          )}
        </div>
      ) : null}
    </section>
  );

  return section;
}

/** Properties of {@link ClockProjectSwitcher}. */
export interface ClockProjectSwitcherProps {
  /** The clock of the signed-in person. */
  readonly control: ClockControl;
}

/**
 * The {@link ProjectSwitcher} bound to the shared clock (F-75), so the start page and the compact
 * clock offer the same choice of project and activity.
 *
 * @param props - Component properties.
 * @returns The selection, or `null` without selectable projects.
 */
export function ClockProjectSwitcher(props: ClockProjectSwitcherProps): JSX.Element | null {
  const { control } = props;

  if (control.projects.length === 0) {
    return null;
  }

  const switcher = (
    <ProjectSwitcher
      state={control.clock.state}
      projects={control.projects}
      choices={control.projectChoices}
      currentProjectId={control.current.projectId}
      clockInProjectId={control.clockInProjectId}
      busy={control.busy}
      onClockInProjectChange={control.chooseClockInProject}
      onSwitch={(projectId) => {
        control.switchProject(projectId);
      }}
      onToggleFavorite={control.toggleFavoriteProject}
      nearlyExhausted={control.nearlyExhausted}
      activitiesOf={control.activitiesOf}
      clockInActivityId={control.clockInActivity}
      onClockInActivityChange={control.chooseClockInActivity}
      currentActivityId={control.current.activityId}
      onSwitchActivity={(activityId) => {
        control.switchProject(control.current.projectId, activityId);
      }}
      requireActivity={control.requireActivity}
    />
  );

  return switcher;
}
